// The language the cabinet is shown in: helpers.js's getLocale/setLocale and the locales list.

import type { Locale } from './state.ts';
import { config } from './config.ts';
import { send } from './http.ts';

const LANG_KEY = 'lang';
const LOCALES_KEY = 'lify.influencer.locales';

export function rememberedLanguage() {
    try {
        return localStorage.getItem(LANG_KEY);
    } catch {
        return null;
    }
}

export function rememberLanguage(name: string | null | undefined): void {
    try {
        if (name) localStorage.setItem(LANG_KEY, name);
    } catch {
        // Chosen for this visit only.
    }
}

/**
 * The remembered language when the API offers it, else the first of the browser's it offers (by base code), else
 * English; always the base code.
 * @param {string[]} supported empty while the list is not known
 */
export function pickLocale(supported: string[]) {
    const hasSupported = supported && supported.length > 0;
    const remembered = rememberedLanguage();

    if (remembered && (!hasSupported || supported.includes(remembered))) return remembered.split('-')[0];

    const languages = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language];
    let found = null;

    if (!hasSupported) {
        found = languages[0] || null;
    } else {
        for (const language of languages) {
            const base = (language || '').split('-')[0];
            const match = supported.find(name => name.split('-')[0] === base);

            if (match) {
                found = match;
                break;
            }
        }
    }

    if (!found) found = hasSupported ? 'en' : (navigator.language || 'en');

    return found.split('-')[0];
}

export function cachedLocales() {
    try {
        const locales = JSON.parse(localStorage.getItem(LOCALES_KEY) || 'null');

        return Array.isArray(locales) ? locales : [];
    } catch {
        return [];
    }
}

export async function fetchLocales(): Promise<Locale[] | null> {
    const response = await send(`${config.mainApiUrl.replace(/\/$/, '')}/locales`);
    const items = response.ok ? response.json<{ items?: Locale[] }>()?.items : null;

    if (!Array.isArray(items)) return null;

    try {
        localStorage.setItem(LOCALES_KEY, JSON.stringify(items));
    } catch {
        // Asked again next visit.
    }

    return items;
}
